import { useCallback } from "react";
import { Card, CardBody, Col, Container, Row, Form, Label } from "reactstrap";
import { useFormik } from "formik";
import { Input, Select } from "../../../../components/Common";
import validationSchema from "./validationSchema";
import { TUser } from "../../../../common/types/user";
import { TProfileForm } from "../../types";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import {
    BusinessTypesOptions,
    MachineApplicationOptions,
    TBusinessType,
    TMachineApplication
} from "../../../../constants/common";
import { TNullable } from "../../../../common/types/common";

import "./styles.scss";

interface Props {
    user: TUser | null;
    companyLogo?: TNullable<File>;
    isDisabled: boolean;
    onSave: (_values: TProfileForm) => void;
}

const ProfileForm = ({
    user,
    companyLogo,
    isDisabled,
    onSave,
}: Props) => {
    const form = useFormik<TProfileForm>({
        initialValues: {
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          email: user?.email || "",
          phone: user?.phone || "",
          company: {
              name: user?.company?.name || "",
              businessType: user?.company?.businessType ? (user?.company?.businessType as TBusinessType) : "",
              machineApplication: user?.company?.machineApplication ? (user?.company?.machineApplication as TMachineApplication) : "",
              file: companyLogo || null,
          },
        },
        enableReinitialize: true,
        validationSchema,
        validateOnMount: true,
        onSubmit: onSave,
    });

    // Handle file selection via dropzone
    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            form.setFieldValue("company.file", acceptedFiles[0]); // Save the first file
        }
    }, []);

    const onResetCompanyLogo = useCallback(() => {
        form.setFieldValue("company.file", null); // Save the first file
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.png'],
        }, // Only allow image files
        maxFiles: 1,
    } as unknown as DropzoneOptions);

    return (
        <Card className="mt-3 user-profile-form">
            <CardBody>
                <Container fluid>
                    <Form
                        onSubmit={e => {
                            e.preventDefault();
                            form.handleSubmit();
                            return false;
                        }}
                    >
                        <Row className="mb-4">
                            <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                                Contact Information
                            </Col>
                            <Col xs={12} xl={6}>
                                <div className="mb-3">
                                    <Input
                                        label="Contact First Name"
                                        name="firstName"
                                        placeholder="Contact First Name"
                                        form={form}
                                        value={form.values.firstName}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} xl={6}>
                                <div className="mb-3">
                                    <Input
                                        label="Contact Last Name"
                                        name="lastName"
                                        placeholder="Contact Last Name"
                                        form={form}
                                        value={form.values.lastName}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col xs={12} xl={6}>
                                <div className="mb-3">
                                    <Input
                                        label="Contact Email Address"
                                        name="email"
                                        placeholder="Contact Email Address"
                                        form={form}
                                        value={form.values.email}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} xl={6}>
                                <div className="mb-3">
                                    <Input
                                        label="Contact Phone Number"
                                        name="phone"
                                        placeholder="Contact Phone Number"
                                        form={form}
                                        value={form.values.phone}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row className="mb-4 d-flex h-100">
                            <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                                Company Information
                            </Col>
                            <Col xs={12} xl={6}>
                                <div className="mb-3">
                                    <Input
                                        label="Company"
                                        name="company.name"
                                        placeholder="Company"
                                        form={form}
                                        value={form.values.company.name}
                                        disabled={isDisabled}
                                    />
                                </div>
                                <div className="mb-3">
                                    <Select
                                        name="company.businessType"
                                        label="Company Business Type"
                                        placeholder="Select Business Type"
                                        options={BusinessTypesOptions}
                                        formik={form}
                                        value = {
                                            BusinessTypesOptions.filter(option =>
                                                option.value === form.values.company.businessType)
                                        }
                                        isDisabled={isDisabled}
                                    ></Select>
                                </div>
                                <div className="mb-3">
                                    <Select
                                        label="Company Machine Application"
                                        name="company.machineApplication"
                                        placeholder="Company Machine Application"
                                        options={MachineApplicationOptions}
                                        formik={form}
                                        value = {
                                            MachineApplicationOptions.filter(option =>
                                                option.value === form.values.company.machineApplication)
                                        }
                                        isDisabled={isDisabled}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} xl={6} className="h-100 d-flex flex-column flex-grow-1">
                                <Label>Company Logo {form.values.company.file && (
                                    <button
                                        className="btn text-primary"
                                        onClick={onResetCompanyLogo}
                                    >
                                        Remove
                                    </button>
                                )}</Label>

                                {form.values.company.file
                                    ? (
                                        <div className="mt-3">
                                            <img
                                                src={URL.createObjectURL(form.values.company.file)}
                                                alt="Preview"
                                                style={{ maxWidth: "100px", maxHeight: "100px" }}
                                            />
                                        </div>
                                    )
                                    : (
                                        <div
                                            {...getRootProps()}
                                            className="drag-and-drop-area"
                                        >
                                            <input
                                                name="company.file"
                                                {...getInputProps()}
                                            />
                                            <span>
                                                {isDragActive ? "Drop the image here..." : "Drag & drop an image here, or click to select"}
                                            </span>
                                        </div>
                                    )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="text-center mt-3">
                                    <button
                                        type="submit"
                                        className="btn btn-danger save-user"
                                        disabled={isDisabled}
                                    >
                                        SAVE
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            </CardBody>
        </Card>
    );
};

export default ProfileForm;
